import React from 'react';
import { useFragment } from 'react-relay/hooks';

import {
  Checkbox,
  createStyles,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import { ListItemProps } from '@material-ui/core/ListItem';
import { CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon } from '@material-ui/icons';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { ErrorBoundary, Typography } from 'embeds/components';

import IconTelehealthOnline from 'icons/IconTelehealthOnline';

import { ListItemChatActivity_EmbedChatActivity$key } from './__generated__/ListItemChatActivity_EmbedChatActivity.graphql';
import AvatarChatActivityReassign from './AvatarChatActivityReassign';
import AvatarNode from './AvatarNode';
import TypographyChatActivityLastMessage from './TypographyChatActivityLastMessage';
import TypographyChatActivityUpdatedAt from './TypographyChatActivityUpdatedAt';

const styles = ({
  spacing: { unit },
  custom: {
    palette: { colorSupportPowder, colorSupportSteel },
  },
  palette: { divider },
}: Theme) =>
  createStyles({
    highlighted: {},
    listItem: {
      minHeight: 70,
      borderBottom: `1px solid ${divider}`,
      '&$selected': {
        backgroundColor: colorSupportPowder,
        '&:hover': {
          backgroundColor: colorSupportPowder,
        },
      },
    },
    listItemWithPaddingLeft: {
      paddingLeft: unit * 2.5,
    },
    checkbox: {
      color: colorSupportSteel,
    },
    selected: {},
    listItemText: {
      paddingLeft: 0,
    },
    listItemTextPrimaryWithMargin: {
      marginRight: unit * 5,
    },
    listItemTextSecondary: {
      display: 'flex',
      flex: 1,
      alignItems: 'flex-end',
    },
    listItemTextSecondaryLastMessage: {
      flex: 1,
      marginRight: unit,
    },
    avatarChatActivityReassign: {
      position: 'absolute',
      top: unit + 2,
      right: unit * 2.5,
    },
  });

export interface ListItemChatActivityProps extends Omit<ListItemProps, 'classes'>, WithStyles<typeof styles> {
  fragmentRef: ListItemChatActivity_EmbedChatActivity$key;
  checked?: boolean;
  highlighted?: boolean;
  onChatSelect?: (id: string, checked?: boolean) => void;
  includeCheckbox?: boolean;
  includeAssignee?: boolean;
  onChatActivityAction?: (action: 'reassign', response: any) => void;
}

const ListItemChatActivity: React.FC<ListItemChatActivityProps> = ({
  classes,
  fragmentRef,
  checked,
  highlighted,
  onChatSelect,
  includeCheckbox,
  selected,
  includeAssignee,
  onChatActivityAction = () => {},
  ...rest
}) => {
  const chatActivity = useFragment<ListItemChatActivity_EmbedChatActivity$key>(
    graphql`
      fragment ListItemChatActivity_EmbedChatActivity on ChatActivity
        @argumentDefinitions(includeAssignee: { type: "Boolean!" }) {
        _id
        patient {
          _id
          firstname
          lastname
        }
        ...AvatarNode_EmbedNode
        ...TypographyChatActivityLastMessage_EmbedChatActivity
        ...TypographyChatActivityUpdatedAt_EmbedChatActivity
        ...AvatarChatActivityReassign_EmbedChatActivity @include(if: $includeAssignee)
      }
    `,
    fragmentRef,
  );

  // TODO Support `isOnTelehealthSession`
  // https://app.clickup.com/t/6rkaj9
  const isOnTelehealthSession = false;

  return (
    <ListItem
      component="div"
      disableGutters
      className={classnames(classes.listItem, {
        [classes.selected]: selected,
        [classes.highlighted]: highlighted,
        [classes.listItemWithPaddingLeft]: !includeCheckbox,
      })}
      {...rest}
    >
      {includeCheckbox && (
        // TODO create atom for Checkbox with `fontSize="small"`
        <Checkbox
          color="primary"
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          className={classes.checkbox}
          checked={checked}
          tabIndex={-1}
          disableRipple
          onClick={(event) => {
            event.stopPropagation();
          }}
          onChange={() => {
            if (onChatSelect) {
              onChatSelect(chatActivity._id, !checked);
            }
          }}
          inputProps={{ 'data-lh-id': 'list-item-chat-activity-checkbox' }}
        />
      )}
      <ListItemIcon>
        <AvatarNode fragmentRef={chatActivity} />
      </ListItemIcon>
      {chatActivity.patient ? (
        <ListItemText
          className={classes.listItemText}
          disableTypography
          primary={
            <Typography
              className={classnames({ [classes.listItemTextPrimaryWithMargin]: includeAssignee })}
              variant={highlighted ? 'body1StrongBold' : 'body1'}
              noWrap
            >
              {chatActivity.patient.lastname}, {chatActivity.patient.firstname}{' '}
              {isOnTelehealthSession && <IconTelehealthOnline />}
            </Typography>
          }
          secondary={
            <Typography component="div" className={classes.listItemTextSecondary} color="textSecondary" noWrap>
              <TypographyChatActivityLastMessage
                fragmentRef={chatActivity}
                className={classes.listItemTextSecondaryLastMessage}
                variant={highlighted ? 'body2StrongBold' : 'body2'}
                color="inherit"
                inline
                noWrap
              />
              <TypographyChatActivityUpdatedAt
                fragmentRef={chatActivity}
                variant="captionStrongBold"
                color="inherit"
                inline
              />
            </Typography>
          }
        />
      ) : (
        // WORKAROUND in case of having wrong data (patient hard deleted)
        <ListItemText
          className={classes.listItemText}
          disableTypography
          primary={<Typography variant="body1">Luma Support</Typography>}
        />
      )}
      {includeAssignee && (
        <div className={classes.avatarChatActivityReassign}>
          <AvatarChatActivityReassign
            fragmentRef={chatActivity}
            size="small"
            mutationConfig={{ callbackSuccess: (response) => onChatActivityAction('reassign', response) }}
          />
        </div>
      )}
    </ListItem>
  );
};

export default withStyles(styles)((props: ListItemChatActivityProps) => (
  <ErrorBoundary fallback={null}>
    <ListItemChatActivity {...props} />
  </ErrorBoundary>
));
